import React, { useState } from 'react';
import { MessageCircle, Mail, CheckCircle2, ArrowRight, Loader2, Sparkles } from 'lucide-react';
import axios from 'axios';
import { trackEvent } from '../../utils/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * PostAnswerActions — shown directly below the AnswerDisplay.
 *
 * Two sections per user's Feb 2026 spec:
 *  1) Report delivery options (WhatsApp + Email checkboxes)
 *  2) Contextual upgrade card matching the customer's next natural step
 *     ₹99 → ₹299 → ₹999 → ₹5,100 (only ONE upgrade card at a time)
 *
 * Backend contract: POST /api/poocho-guruji/deliver-report
 *   body: { session_id, deliver_whatsapp, deliver_email, phone, email }
 *   (If backend endpoint doesn't exist yet, the component still lets user
 *    tick options and shows success — a background beacon logs the intent
 *    to analytics so we can wire the real delivery later.)
 */

// Progressive upgrade ladder — one step per level. Only current + next visible.
const UPGRADE_LADDER = {
  quick: {
    to: 'deep',
    price: 299,
    label: 'Deep Analysis',
    label_hi: '₹299 Deep Analysis',
    prompt_hi: 'और गहराई से जानना चाहते हैं?',
    bullets_hi: [
      '3 प्रश्नों का विस्तृत उत्तर (₹99 में 1 प्रश्न था)',
      'पूर्ण Vimshottari Dasha timing analysis',
      'Yogas और Doshas का complete mapping',
      'PDF रिपोर्ट + WhatsApp delivery',
      'FREE follow-up doubt clarification',
    ],
    cta_hi: '₹299 में Deep Analysis पाएँ',
  },
  deep: {
    to: 'premium',
    price: 999,
    label: 'Premium (30-day)',
    label_hi: '₹999 Premium Analysis',
    prompt_hi: 'क्या आप पूरी कुंडली और दशा के साथ विस्तृत विश्लेषण चाहते हैं?',
    bullets_hi: [
      '30 दिनों तक प्रतिदिन 1 व्यक्तिगत ज्योतिषीय मार्गदर्शन',
      'Dasha + Gochar + Kundli आधारित उत्तर',
      'हर प्रश्न पर पराशरी योग/दोष का विश्लेषण',
      'Kundli, प्रश्न, उत्तर History स्वतः सुरक्षित',
      'WhatsApp Dashboard access',
    ],
    cta_hi: '₹999 में Premium शुरू करें',
  },
  premium: {
    to: 'personal',
    price: 5100,
    label: 'Personal Consultation',
    label_hi: '₹5,100 Personal Consultation',
    prompt_hi: 'अब आचार्य विवेक जी से व्यक्तिगत परामर्श लें',
    bullets_hi: [
      '90-मिनट In-person (Faridabad) / Video call',
      'Complete life reading (Career/Marriage/Health)',
      'Full Varga analysis (D-9, D-10, D-7, D-20)',
      'Custom Yantra + Rudraksha guidance',
      '3-month WhatsApp follow-up support',
    ],
    cta_hi: '₹5,100 में परामर्श बुक करें',
  },
  personal: null, // top of ladder
};

export default function PostAnswerActions({ sessionId, defaultPhone, defaultEmail, currentTier, autoDelivery, onUpgrade }) {
  // Auto-delivery outcome (from parent's session-status poll response):
  //   { whatsapp:{attempted, sent, failed, skipped_reason}, email:{attempted, sent} }
  // Feb 2026 — used to show a green ✅ "already sent" badge OR an eye-catching
  // amber "Resend" retry button so first-time hiccups are recoverable in one tap.
  const waAutoOK = !!(autoDelivery?.whatsapp?.attempted && autoDelivery.whatsapp.sent > 0);
  const emAutoOK = !!(autoDelivery?.email?.attempted && autoDelivery.email.sent);
  const waAutoFailed = !!(autoDelivery?.whatsapp?.attempted && autoDelivery.whatsapp.sent === 0);
  const emAutoFailed = !!(autoDelivery?.email?.attempted && !autoDelivery.email.sent);
  const anyAutoFailure = waAutoFailed || emAutoFailed;
  const bothAutoOK = waAutoOK && (emAutoOK || !autoDelivery?.email?.attempted);

  const [wa, setWa] = useState(!waAutoOK);
  const [em, setEm] = useState(!!defaultEmail && !emAutoOK);
  const [phone, setPhone] = useState(defaultPhone || '');
  const [email, setEmail] = useState(defaultEmail || '');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sentStatus, setSentStatus] = useState(null); // {wa: 'ok'|'skipped'|'failed', em: 'ok'|'failed'}
  const [err, setErr] = useState(null);

  const upgrade = UPGRADE_LADDER[currentTier] || UPGRADE_LADDER.quick;

  const sendReport = async () => {
    setErr(null);
    if (!wa && !em) { setErr('कम से कम एक option चुनें'); return; }
    if (wa && !phone.trim()) { setErr('WhatsApp number ज़रूरी है'); return; }
    if (em && !email.trim()) { setErr('Email ज़रूरी है'); return; }
    setSending(true);
    try {
      trackEvent('poocho_report_delivery_request', {
        session_id: sessionId, whatsapp: wa, email: em,
      });
      // 🔒 Feb 2026 P0 FIX: do NOT swallow the error with `.catch(()=>{})` and
      // do NOT fake a success toast. Real delivery status (Twilio/Brevo)
      // must be surfaced so paid users know if their answer actually went out.
      const res = await axios.post(`${API}/api/poocho-guruji/deliver-report`, {
        session_id: sessionId,
        deliver_whatsapp: wa,
        deliver_email: em,
        phone: wa ? phone.trim() : null,
        email: em ? email.trim() : null,
      });
      const data = res.data || {};
      const waRes = data.whatsapp || null;
      const emRes = data.email || null;
      const waStatus = wa
        ? (waRes && waRes.sent > 0 ? 'ok'
            : waRes && waRes.skipped_reason ? 'skipped'
            : 'failed')
        : null;
      const emStatus = em
        ? (emRes && emRes.sent ? 'ok' : 'failed')
        : null;
      setSentStatus({ wa: waStatus, em: emStatus });
      // If BOTH channels failed, keep the button visible so user can retry.
      const anyOk = waStatus === 'ok' || emStatus === 'ok';
      if (anyOk) {
        setSent(true);
      } else {
        setErr('Delivery failed — कृपया WhatsApp +91-9311369729 पर संपर्क करें।');
      }
    } catch (e) {
      setErr(
        e?.response?.data?.detail
          || 'कुछ गड़बड़ हुई — कुछ देर बाद कोशिश करें'
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 pb-12" data-testid="post-answer-actions">
      {/* ═══ Auto-delivery status banner (Feb 2026) ═══
          Shows the outcome of the automatic WhatsApp/Email delivery that ran
          right after answer generation. Green if it worked (so paid users
          feel confident); amber "one-tap Resend" retry if it failed. */}
      {bothAutoOK && (
        <div
          className="mt-6 rounded-2xl border border-emerald-500/40 bg-emerald-500/[0.08] p-4 flex items-start gap-3"
          data-testid="pg-auto-delivery-ok"
        >
          <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
          <div className="flex-1 text-[13.5px] leading-relaxed">
            <div className="font-semibold text-emerald-300 mb-0.5">
              आपका उत्तर WhatsApp{emAutoOK ? ' और Email' : ''} पर भेज दिया गया है ✓
            </div>
            <div className="text-text-muted">
              2-3 मिनट में पहुँच जाएगा। नीचे नए number/email पर दोबारा भी भेज सकते हैं।
            </div>
          </div>
        </div>
      )}
      {!bothAutoOK && anyAutoFailure && (
        <div
          className="mt-6 rounded-2xl border-2 border-amber-500/60 bg-amber-500/[0.10] p-4"
          data-testid="pg-auto-delivery-retry"
        >
          <div className="flex items-start gap-3 mb-3">
            <MessageCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1 text-[13.5px] leading-relaxed">
              <div className="font-semibold text-amber-300 mb-0.5">
                {autoDelivery?.whatsapp?.skipped_reason === 'sender_equals_recipient'
                  ? 'आप अपने ही (Guruji) number पर test कर रहे हैं'
                  : `आपके ${waAutoFailed && emAutoFailed ? 'WhatsApp और Email' : waAutoFailed ? 'WhatsApp' : 'Email'} पर auto-delivery नहीं हो पाई`}
              </div>
              <div className="text-text-muted">
                {autoDelivery?.whatsapp?.skipped_reason === 'sender_equals_recipient'
                  ? 'Twilio API sender aur receiver same number allow नहीं करता। नीचे कोई भी दूसरा number (family/friend) दे कर एक tap में भेज सकते हैं — real customers पर यह issue कभी नहीं आएगा।'
                  : 'कोई बात नहीं — नीचे number/email verify करके एक tap से दोबारा भेज सकते हैं।'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Report delivery options ═══ */}
      <div className="mt-6 rounded-2xl border border-gold/30 bg-white/[0.03] p-5" data-testid="pg-report-options">
        <h3 className="font-heading text-lg text-gold mb-1">अपनी Report कहाँ भेजें?</h3>
        <p className="text-text-muted text-[13px] mb-4">आप एक या दोनों options चुन सकते हैं।</p>

        <label className="flex items-start gap-3 mb-3 cursor-pointer" data-testid="pg-report-wa-toggle">
          <input type="checkbox" checked={wa} onChange={(e) => setWa(e.target.checked)} className="mt-1 w-4 h-4 accent-gold" />
          <span className="flex-1">
            <span className="inline-flex items-center gap-1.5 font-semibold text-text-main">
              <MessageCircle className="w-4 h-4 text-[#25D366]" /> WhatsApp पर Report प्राप्त करें
            </span>
            {wa && (
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="WhatsApp number (+91…)"
                className="mt-2 w-full rounded-lg bg-white/[0.05] border border-gold/25 px-3 py-2 text-sm text-text-main outline-none focus:border-gold"
                data-testid="pg-report-wa-input"
              />
            )}
          </span>
        </label>

        <label className="flex items-start gap-3 cursor-pointer" data-testid="pg-report-email-toggle">
          <input type="checkbox" checked={em} onChange={(e) => setEm(e.target.checked)} className="mt-1 w-4 h-4 accent-gold" />
          <span className="flex-1">
            <span className="inline-flex items-center gap-1.5 font-semibold text-text-main">
              <Mail className="w-4 h-4 text-gold" /> Email पर Report प्राप्त करें
            </span>
            {em && (
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-2 w-full rounded-lg bg-white/[0.05] border border-gold/25 px-3 py-2 text-sm text-text-main outline-none focus:border-gold"
                data-testid="pg-report-email-input"
              />
            )}
          </span>
        </label>

        {err && <p className="text-red-400 text-xs mt-3" data-testid="pg-report-err">{err}</p>}
        {sent ? (
          <div className="mt-4 space-y-1.5 text-sm" data-testid="pg-report-sent">
            {sentStatus?.wa === 'ok' && (
              <div className="flex items-center gap-2 text-emerald-400" data-testid="pg-report-wa-ok">
                <CheckCircle2 className="w-4 h-4" /> WhatsApp पर भेज दिया गया — 2-3 मिनट में आ जाएगा।
              </div>
            )}
            {sentStatus?.wa === 'failed' && (
              <div className="flex items-center gap-2 text-amber-400" data-testid="pg-report-wa-fail">
                <CheckCircle2 className="w-4 h-4 opacity-40" /> WhatsApp delivery अभी नहीं जा पाई — कृपया WhatsApp +91-9311369729 पर संपर्क करें।
              </div>
            )}
            {sentStatus?.wa === 'skipped' && (
              <div className="flex items-center gap-2 text-amber-400" data-testid="pg-report-wa-skip">
                <CheckCircle2 className="w-4 h-4 opacity-40" /> WhatsApp skip हो गया (sender/receiver same number) — कृपया दूसरा number दें।
              </div>
            )}
            {sentStatus?.em === 'ok' && (
              <div className="flex items-center gap-2 text-emerald-400" data-testid="pg-report-em-ok">
                <CheckCircle2 className="w-4 h-4" /> Email भेज दी गई — Inbox / Spam folder दोनों check करें।
              </div>
            )}
            {sentStatus?.em === 'failed' && (
              <div className="flex items-center gap-2 text-amber-400" data-testid="pg-report-em-fail">
                <CheckCircle2 className="w-4 h-4 opacity-40" /> Email delivery अभी नहीं जा पाई — कृपया WhatsApp +91-9311369729 पर संपर्क करें।
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={sendReport}
            disabled={sending}
            className="mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold text-[#2c0b14] text-[14px] shadow-lg active:scale-[0.98] transition-transform disabled:opacity-60"
            style={{ background: 'linear-gradient(90deg,#F4D477,#F1C40F,#B8942D)' }}
            data-testid="pg-report-send-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            {sending
              ? 'भेजा जा रहा है…'
              : anyAutoFailure
                ? 'दोबारा भेजें'
                : 'Report भेजें'}
          </button>
        )}
      </div>

      {/* ═══ Contextual upgrade card (only ONE — next tier) ═══ */}
      {upgrade && (
        <div
          className="mt-8 rounded-3xl overflow-hidden shadow-2xl"
          style={{
            background: 'linear-gradient(135deg,#2c0b14 0%,#3d101e 50%,#5a1a2e 100%)',
            border: '1px solid rgba(244,196,54,0.45)',
          }}
          data-testid={`pg-upgrade-card-${upgrade.to}`}
        >
          <div className="p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-[#F4D477]" />
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#F4D477] font-semibold">अगला कदम</span>
            </div>
            <h3 className="font-heading text-2xl sm:text-3xl text-white leading-snug mb-1">
              {upgrade.prompt_hi}
            </h3>
            <p className="font-heading text-lg text-[#F4D477] mb-4">{upgrade.label_hi}</p>

            <ul className="space-y-2 mb-6">
              {upgrade.bullets_hi.map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-[#F8E5D5] text-[13.5px]">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-[#F4D477]" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => onUpgrade && onUpgrade(upgrade.to)}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full font-bold text-[#2c0b14] text-[15px] tracking-wide shadow-xl active:scale-[0.98] transition-transform"
              style={{ background: 'linear-gradient(90deg,#F4D477,#F1C40F,#B8942D)' }}
              data-testid={`pg-upgrade-cta-${upgrade.to}`}
            >
              {upgrade.cta_hi}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
